import { LoadingButton } from "@mui/lab";
import { Divider, Stack, Tabs, Tab, Box } from "@mui/material";
import { useCallback, useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import * as yup from "yup";
import { yupResolver } from "@hookform/resolvers/yup";
import _ from "lodash";
import { useNavigate, useLocation, Routes, Route } from "react-router-dom";
import profileValidations from "./schema";
import Fields from "./Fields";
import { Alert, ErrorAlert } from "../../utils";
import { useAuth } from "../../auth/context";
import { toDirtyValues } from "../../utils/form";
import DoctorInfo from "./DoctorInfo";
import { useApi } from "../../utils/hooks";
import TitleHeadSess from "../../utils/TitleHeadSess";

const errorMessage =
  "No pudimos actualizar tus datos. Por favor, intentalo más tarde";
const successMessage = "Tus datos fueron actualizados con éxito";

const formSchema = yup.object().shape(profileValidations);

const SettingsNavigation = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const currentPath = location.pathname;

  const handleChange = (event, newValue) => {
    navigate(newValue);
  };

  return (
    <Box sx={{ borderBottom: 1, borderColor: "divider", mb: 3 }}>
      <Tabs
        value={currentPath}
        onChange={handleChange}
        aria-label="configuración"
      >
        <Tab label="Datos Personales" value="/settings" />
      </Tabs>
    </Box>
  );
};

const MainSettings = () => {
  const { user } = useAuth();
  const form = useForm({
    defaultValues: _.pick(user.profile, _.keys(profileValidations)),
    resolver: yupResolver(formSchema),
    context: { user },
  });
  const [updateStatus, setUpdateStatus] = useState(null);
  const [getSignatureStatus, setGetSignatureStatus] = useState(null);
  const {
    handleSubmit,
    formState: { dirtyFields },
  } = form;
  const api = useApi();

  const onAlertClose = () => setUpdateStatus(null);
  const isLoading = updateStatus === "loading";
  const failed = updateStatus === "error";
  const succeeded = updateStatus === "success";

  const signatureLoading = getSignatureStatus === "loading";

  useEffect(() => {
    const fetchSignature = async () => {
      setGetSignatureStatus("loading");
      try {
        const signature = await api.signature();
        form.setValue("signature", signature);
        setGetSignatureStatus("success");
      } catch (error) {
        setGetSignatureStatus("error");
      }
    };

    fetchSignature();
  }, []); // Run only once on mount

  const updateUser = useCallback(
    (fullProfile) => {
      const profileToUpdate = toDirtyValues(dirtyFields, fullProfile);
      setUpdateStatus("loading");
      api
        .updateUser({
          profile: {
            ..._.omit(profileToUpdate, "signature"),
            id: user.profile.id,
          },
          signature: profileToUpdate.signature,
        })
        .then(() => {
          setUpdateStatus("success");
        })
        .catch(() => {
          setUpdateStatus("error");
        });
    },
    [api, dirtyFields, user.profile.id]
  );

  const canSave = !_.isEmpty(dirtyFields);

  return (
    <Stack
      width="100%"
      spacing="1rem"
      sx={{ p: { xs: "1rem", sm: "2rem" }, mt: { xs: "1rem" } }}
    >
      <form
        onSubmit={handleSubmit(updateUser)}
        autoComplete="off"
        style={{ width: "100%" }}
      >
        <Stack spacing="2rem">
          <DoctorInfo />
          <Divider />
          <Fields form={form} signatureLoading={signatureLoading} />
        </Stack>

        <Stack
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            flexDirection: "row",
            mt: 2,
            mb: 4,
          }}
        >
          <LoadingButton
            sx={{ ml: 1 }}
            type="submit"
            variant="contained"
            loading={isLoading}
            disabled={!canSave}
          >
            Guardar
          </LoadingButton>
        </Stack>
      </form>
      <ErrorAlert show={failed} onClose={onAlertClose} message={errorMessage} />
      <Alert
        severity="success"
        message={successMessage}
        show={succeeded}
        onClose={onAlertClose}
        duration={2000}
      />
    </Stack>
  );
};

const Settings = () => {
  return (
    <Stack
      width="100%"
      spacing="1rem"
      sx={{ p: { xs: "1rem", sm: "2rem" }, mt: { xs: "1rem" } }}
    >
      <TitleHeadSess title={"Ajustes"} />
      <SettingsNavigation />

      <Routes>
        <Route index element={<MainSettings />} />
      </Routes>
    </Stack>
  );
};

export default Settings;
